/**
 * Scores Page
 *
 * Generate scores and view results with evidence spans.
 */

import { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { apiClient } from '../lib/api';

import { RequestPanel, StatusBadge } from '../components/RequestPanel';
import { JsonViewer, Code } from '../components/JsonViewer';
import type { ApiResult, GenerateScoreResponse, ScoringJob, ScoreListResponse } from '../lib/types';

export function Scores() {
  const [searchParams] = useSearchParams();

  // Form state
  const [transcriptVersionId, setTranscriptVersionId] = useState(
    searchParams.get('transcriptArtifactVersionId') ?? ''
  );
  const [rubricId, setRubricId] = useState(searchParams.get('rubricId') ?? '');
  const [rubricVersion, setRubricVersion] = useState('');

  // Results
  const [generating, setGenerating] = useState(false);
  const [generateResult, setGenerateResult] = useState<ApiResult<GenerateScoreResponse> | null>(null);
  const [, setScoreJob] = useState<ScoringJob | null>(null);

  // Lookup by transcript
  const [lookupId, setLookupId] = useState('');
  const [lookupResult, setLookupResult] = useState<ApiResult<ScoreListResponse> | null>(null);
  const [lookupScores, setLookupScores] = useState<ScoringJob[]>([]);
  const [lookupLoading, setLookupLoading] = useState(false);

  // Selected score for detail view
  const [selectedScore, setSelectedScore] = useState<ScoringJob | null>(null);

  const handleGenerate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!transcriptVersionId.trim()) return;

    setGenerating(true);
    setGenerateResult(null);
    setScoreJob(null);

    const res = await apiClient.generateScore({
      transcriptArtifactVersionId: transcriptVersionId.trim(),
      rubricId: rubricId.trim() || undefined,
      rubricVersion: rubricVersion ? parseInt(rubricVersion, 10) : undefined,
    });

    setGenerateResult(res);
    setGenerating(false);

    // If successful, fetch the full scoring job for evidence
    if (res.response.data) {
      const scoresRes = await apiClient.getScoresByTranscript(transcriptVersionId.trim());
      if (scoresRes.response.data?.items.length) {
        const job = scoresRes.response.data.items.find(
          (j) => j.id === res.response.data?.jobId
        );
        if (job) {
          setScoreJob(job);
          setSelectedScore(job);
        }
      }
    }
  };

  const handleLookup = async () => {
    if (!lookupId.trim()) return;
    setLookupLoading(true);
    setLookupResult(null);
    setLookupScores([]);

    const res = await apiClient.getScoresByTranscript(lookupId.trim());
    setLookupResult(res);
    if (res.response.data) {
      setLookupScores(res.response.data.items);
    }
    setLookupLoading(false);
  };

  // Auto-lookup if transcriptArtifactVersionId is in URL
  useEffect(() => {
    const versionId = searchParams.get('transcriptArtifactVersionId');
    if (versionId) {
      setLookupId(versionId);
      apiClient.getScoresByTranscript(versionId).then((res) => {
        setLookupResult(res);
        if (res.response.data) {
          setLookupScores(res.response.data.items);
        }
      });
    }
  }, [searchParams]);

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ fontSize: 30, fontWeight: 600, letterSpacing: '-0.025em', color: 'var(--slate-900)', marginBottom: 4 }}>
          Scores
        </h1>
        <p style={{ color: 'var(--slate-500)', fontSize: 14, margin: 0 }}>
          Generate and view transcript scoring results
        </p>
      </div>

      {/* Generate Score Form */}
      <div className="card">
        <h2>Generate Score</h2>
        <form onSubmit={handleGenerate}>
          <div className="form-group">
            <label className="label">Transcript Artifact Version ID *</label>
            <input
              type="text"
              className="input mono"
              value={transcriptVersionId}
              onChange={(e) => setTranscriptVersionId(e.target.value)}
              placeholder="Enter ULID of transcript artifact version"
              required
            />
            <p className="text-muted text-small" style={{ marginTop: 4 }}>
              The version ID of a committed text/plain transcript artifact
            </p>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <div className="form-group">
              <label className="label">Rubric ID (optional)</label>
              <input
                type="text"
                className="input mono"
                value={rubricId}
                onChange={(e) => setRubricId(e.target.value)}
                placeholder="Uses first published if blank"
              />
            </div>
            <div className="form-group">
              <label className="label">Rubric Version (optional)</label>
              <input
                type="number"
                className="input"
                value={rubricVersion}
                onChange={(e) => setRubricVersion(e.target.value)}
                placeholder="Latest if blank"
              />
            </div>
          </div>

          <button type="submit" className="btn btn-primary" disabled={generating}>
            {generating ? 'Generating...' : 'Generate Score'}
          </button>
        </form>

        <RequestPanel result={generateResult} loading={generating} />

        {/* Score Results */}
        {generateResult?.response.data && (
          <div style={{ marginTop: 16 }}>
            <h3>Score Generated</h3>
            <table className="table" style={{ maxWidth: 500 }}>
              <tbody>
                <tr>
                  <td style={{ fontWeight: 500 }}>Job ID</td>
                  <td><Code>{generateResult.response.data.jobId}</Code></td>
                </tr>
                <tr>
                  <td style={{ fontWeight: 500 }}>Overall Score</td>
                  <td style={{ fontSize: 18, fontWeight: 600 }}>
                    {generateResult.response.data.overallScore.toFixed(2)}
                  </td>
                </tr>
                <tr>
                  <td style={{ fontWeight: 500 }}>Normalized Score</td>
                  <td>{(generateResult.response.data.normalizedScore * 100).toFixed(1)}%</td>
                </tr>
                <tr>
                  <td style={{ fontWeight: 500 }}>Passed</td>
                  <td>
                    <span
                      style={{
                        color: generateResult.response.data.passed ? '#16a34a' : '#dc2626',
                        fontWeight: 600,
                      }}
                    >
                      {generateResult.response.data.passed ? 'YES' : 'NO'}
                    </span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Lookup Scores */}
      <div className="card">
        <h2>Lookup Scores by Transcript</h2>
        <div className="flex gap-2" style={{ marginBottom: 16 }}>
          <input
            type="text"
            className="input mono"
            style={{ flex: 1 }}
            value={lookupId}
            onChange={(e) => setLookupId(e.target.value)}
            placeholder="Transcript Artifact Version ID"
          />
          <button
            className="btn btn-secondary"
            onClick={handleLookup}
            disabled={lookupLoading || !lookupId.trim()}
          >
            {lookupLoading ? 'Loading...' : 'Lookup'}
          </button>
        </div>

        {lookupScores.length > 0 && (
          <table className="table">
            <thead>
              <tr>
                <th>Job ID</th>
                <th>Status</th>
                <th>Overall Score</th>
                <th>Passed</th>
                <th>Created At</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {lookupScores.map((job) => (
                <tr key={job.id}>
                  <td><Code>{job.id.slice(0, 12)}...</Code></td>
                  <td><StatusBadge status={job.status} /></td>
                  <td style={{ fontWeight: 600 }}>
                    {job.result?.overallScore.toFixed(2) ?? '-'}
                  </td>
                  <td>
                    {job.result && (
                      <span style={{ color: job.result.passed ? '#16a34a' : '#dc2626' }}>
                        {job.result.passed ? 'YES' : 'NO'}
                      </span>
                    )}
                  </td>
                  <td className="text-muted">{new Date(job.createdAt).toLocaleString()}</td>
                  <td>
                    <button
                      className="btn btn-secondary"
                      style={{ padding: '4px 8px', fontSize: 12 }}
                      onClick={() => setSelectedScore(job)}
                    >
                      View Details
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        <RequestPanel result={lookupResult} loading={lookupLoading} />
      </div>

      {/* Score Detail View */}
      {selectedScore?.result && (
        <div className="card">
          <div className="flex justify-between items-center">
            <h2>Score Details</h2>
            <button
              className="btn btn-secondary"
              style={{ padding: '4px 8px', fontSize: 12 }}
              onClick={() => setSelectedScore(null)}
            >
              Close
            </button>
          </div>

          <table className="table" style={{ maxWidth: 400, marginBottom: 16 }}>
            <tbody>
              <tr>
                <td style={{ fontWeight: 500 }}>Overall Score</td>
                <td style={{ fontSize: 18, fontWeight: 600 }}>
                  {selectedScore.result.overallScore.toFixed(2)} / {selectedScore.result.maxPossibleScore.toFixed(2)}
                </td>
              </tr>
              <tr>
                <td style={{ fontWeight: 500 }}>Normalized</td>
                <td>{(selectedScore.result.normalizedScore * 100).toFixed(1)}%</td>
              </tr>
              <tr>
                <td style={{ fontWeight: 500 }}>Passed</td>
                <td style={{ color: selectedScore.result.passed ? '#16a34a' : '#dc2626', fontWeight: 600 }}>
                  {selectedScore.result.passed ? 'YES' : 'NO'}
                </td>
              </tr>
              <tr>
                <td style={{ fontWeight: 500 }}>Execution Time</td>
                <td>{selectedScore.result.executionTimeMs}ms</td>
              </tr>
            </tbody>
          </table>

          <h3>Dimension Scores</h3>
          {selectedScore.result.dimensionScores.map((dim) => (
            <div
              key={dim.dimensionId}
              style={{
                marginBottom: 16,
                padding: 12,
                background: '#f9f9f9',
                borderRadius: 6,
              }}
            >
              <div className="flex justify-between items-center" style={{ marginBottom: 8 }}>
                <strong>{dim.dimensionId}</strong>
                <span>
                  Score: <strong>{dim.score.toFixed(2)}</strong> / {dim.maxScore.toFixed(2)}
                  {' '}(weighted: {dim.weightedScore.toFixed(2)})
                </span>
              </div>

              {dim.evidence.length > 0 && (
                <div>
                  <div className="text-small text-muted" style={{ marginBottom: 4 }}>
                    Evidence ({dim.evidence.length} spans):
                  </div>
                  <table className="table" style={{ fontSize: 12 }}>
                    <thead>
                      <tr>
                        <th>Rule</th>
                        <th>Matched</th>
                        <th>Text</th>
                        <th>Position</th>
                        <th>Segment</th>
                      </tr>
                    </thead>
                    <tbody>
                      {dim.evidence.map((ev, i) => (
                        <tr key={i}>
                          <td><Code>{ev.ruleId}</Code></td>
                          <td>
                            <span style={{ color: ev.matched ? '#16a34a' : '#dc2626' }}>
                              {ev.matched ? '✓' : '✗'}
                            </span>
                          </td>
                          <td
                            className="mono"
                            style={{
                              maxWidth: 200,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                            title={ev.matchedText}
                          >
                            "{ev.matchedText}"
                          </td>
                          <td className="mono text-muted">
                            {ev.charStart}-{ev.charEnd}
                          </td>
                          <td className="mono text-muted">
                            {ev.segmentId !== undefined ? (
                              <>
                                #{ev.segmentId}
                                {ev.segmentStart !== undefined && (
                                  <> ({ev.segmentStart.toFixed(2)}s - {ev.segmentEnd?.toFixed(2)}s)</>
                                )}
                              </>
                            ) : (
                              '-'
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          ))}

          <h3>Full Result JSON</h3>
          <JsonViewer data={selectedScore.result} maxHeight={400} />
        </div>
      )}

      {/* Link to related artifacts */}
      <div className="card">
        <h2>Related Actions</h2>
        <div className="flex gap-2">
          <Link to="/console/artifacts" className="btn btn-secondary">
            Browse Artifacts
          </Link>
          <Link to="/console/rubrics" className="btn btn-secondary">
            View Rubrics
          </Link>
        </div>
      </div>
    </div>
  );
}
